import { GetGpuAdapterInfo } from '../wgs/wgpu/globals';

/**
 * @typedef {Object} GpuPerformanceResult
 * @property {boolean} isSlowGpu - True when a slow/integrated GPU is detected.
 * @property {'integrated-gpu'|'software-renderer'} reason
 * @property {{ vendor: string, renderer: string }} rendererInfo - GPU vendor and renderer strings.
 * @private
 */

// Renderer string fragments that indicate a software or integrated GPU.
// Matching is case-insensitive.
const SOFTWARE_PATTERNS = [
/swiftshader/i,
/llvmpipe/i,
/microsoft basic render/i,
/softpipe/i];


const INTEGRATED_PATTERNS = [
/intel.*uhd/i,
/intel.*hd graphics/i,
/intel.*iris/i,
/intel\(r\) graphics/i,
// Matches integrated Intel Arc GPUs (e.g. "Intel Arc Graphics") while excluding discrete Arc models:
//   - Pro A-series (Pro A40, Pro A50, Pro A60M)
//   - Consumer discrete with 3-digit model numbers (A380, A770, B580, B580M)
//   - Models ending in T (desktop low-power, e.g. A310T) or V (workstation, e.g. A370V)
/Intel.*Arc(?!.*\b(?:Pro\s+A\d{2}(?:M)?|[AB]\d{3}(?:M)?)\b).*(?:Graphics|\d{3}[TV]\b|GPU)/i];


/**
 * Synchronous detection based on the WebGL renderer string.
 *
 * @param {string} vendor
 * @param {string} renderer
 * @returns {GpuPerformanceResult|null} null when no slow GPU is detected.
 * @private
 */
export function detectFromRendererString(vendor, renderer) {
  const rendererInfo = { vendor: vendor || '', renderer: renderer || '' };

  for (const pattern of SOFTWARE_PATTERNS) {
    if (pattern.test(renderer)) {
      return { isSlowGpu: true, reason: 'software-renderer', rendererInfo };
    }
  }

  for (const pattern of INTEGRATED_PATTERNS) {
    if (pattern.test(renderer)) {
      return { isSlowGpu: true, reason: 'integrated-gpu', rendererInfo };
    }
  }

  return null;
}

/**
 * Synchronous detection based on the WebGPU adapter info.
 * Returns null when WebGPU adapter info is unavailable (i.e. WebGPU is not in use).
 *
 * @param {Object} [overrideDeviceInfo] - Optional override device info.
 * @param {string} overrideDeviceInfo.webgpu.vendor - Optional override vendor.
 * @param {string} overrideDeviceInfo.webgpu.architecture - Optional override architecture.
 * @param {boolean} overrideDeviceInfo.webgpu.isFallbackAdapter - Optional override isFallbackAdapter indicator.
 * @returns {GpuPerformanceResult|null} null when no slow GPU is detected.
 * @private
 */
export function detectFromWebGPUInfo(overrideDeviceInfo = null) {
  const adapterInfo = overrideDeviceInfo?.webgpu || GetGpuAdapterInfo();
  if (!adapterInfo) {
    return null;
  }

  const rendererInfo = {
    vendor: adapterInfo.vendor || '',
    renderer: adapterInfo.architecture || ''
  };

  if (adapterInfo.isFallbackAdapter) {
    return { isSlowGpu: true, reason: 'software-renderer', rendererInfo };
  }

  // Intel discrete (Arc) GPUs use high-performance architectures (xe-hpg, xe2-hpg).
  // All other Intel GPUs are integrated and should be flagged.
  if (/^intel$/i.test(adapterInfo.vendor) && !/hpg/i.test(adapterInfo.architecture)) {
    return { isSlowGpu: true, reason: 'integrated-gpu', rendererInfo };
  }

  return null;
}